import axiosInstance from '@/app/api/axiosInstance';
import {
  Godown, GodownCreate, GodownUpdate,
  UnitConversion, UnitConversionCreate, UnitConversionUpdate,
  CurrentStock, StockTransferRequest, StockTransferResponse,
  StockReconciliationRequest, ItemStockSummaryResponse, StockLedgerEntry,
  StockSheetItem, BulkReconcileRequest, BulkReconcileResponse,
  StockLedgerStatementParams, StockLedgerStatementResponse
} from '@/app/types/inventory';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// --- Godown Service Functions ---

export const listGodowns = async (
  params: ListQueryParams
): Promise<PaginatedResponse<Godown>> => {
  try {
    const requestParams = { ...params };
    // Backend filters by org via auth
    const response = await axiosInstance.get<PaginatedResponse<Godown>>('/godowns/', {
      params: requestParams
    });
    return response.data;
  } catch (error) {
    console.error('Failed to list godowns:', error);
    throw error;
  }
};

export const createGodown = async (data: GodownCreate): Promise<Godown> => {
  try {
    // Backend infers organization_id from the authenticated user
    const response = await axiosInstance.post<Godown>('/godowns/', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create godown:', error);
    throw error;
  }
};

export const updateGodown = async (godownId: number, data: GodownUpdate): Promise<Godown> => {
  try {
    const response = await axiosInstance.patch<Godown>(`/godowns/${godownId}`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update godown with ID ${godownId}:`, error);
    throw error;
  }
};

export const deleteGodown = async (godownId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/godowns/${godownId}`);
  } catch (error) {
    console.error(`Failed to delete godown with ID ${godownId}:`, error);
    throw error;
  }
};

// --- Unit Conversion Service Functions ---

export const listUnitConversions = async (
  params: ListQueryParams
): Promise<PaginatedResponse<UnitConversion>> => {
  try {
    const requestParams = { ...params };
    // Backend filters by org via auth
    const response = await axiosInstance.get<PaginatedResponse<UnitConversion>>('/unit-conversions/', {
      params: requestParams
    });
    // Convert conversion_factor from string (if backend sends string) to number
    response.data.items = response.data.items.map(item => ({
        ...item,
        conversion_factor: parseFloat(String(item.conversion_factor))
    }));
    return response.data;
  } catch (error) {
    console.error('Failed to list unit conversions:', error);
    throw error;
  }
};

export const createUnitConversion = async (data: UnitConversionCreate): Promise<UnitConversion> => {
  try {
    // Ensure conversion_factor is sent as a number/string as expected by backend
    const payload = {
        ...data,
        conversion_factor: String(data.conversion_factor) // Send as string if backend expects Decimal
    };
    const response = await axiosInstance.post<UnitConversion>('/unit-conversions/', payload);
    // Convert response factor to number
    response.data.conversion_factor = parseFloat(String(response.data.conversion_factor));
    return response.data;
  } catch (error) {
    console.error('Failed to create unit conversion:', error);
    throw error;
  }
};

export const updateUnitConversion = async (conversionId: number, data: UnitConversionUpdate): Promise<UnitConversion> => {
  try {
      const payload = {
          ...data,
          ...(data.conversion_factor !== undefined && { conversion_factor: String(data.conversion_factor) }) // Send as string if backend expects Decimal
      };
    const response = await axiosInstance.patch<UnitConversion>(`/unit-conversions/${conversionId}`, payload);
    // Convert response factor to number
    response.data.conversion_factor = parseFloat(String(response.data.conversion_factor));
    return response.data;
  } catch (error) {
    console.error(`Failed to update unit conversion with ID ${conversionId}:`, error);
    throw error;
  }
};

export const deleteUnitConversion = async (conversionId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/unit-conversions/${conversionId}`);
  } catch (error) {
    console.error(`Failed to delete unit conversion with ID ${conversionId}:`, error);
    throw error;
  }
};

// --- Current Stock Service Functions ---

export interface StockQueryParams {
  item_id?: number;
  godown_id?: number;
  min_quantity?: number;
  max_quantity?: number;
  skip?: number;
  limit?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  filters?: Record<string, any>;
  search_query?: string;
}

export const getCurrentStock = async (params: StockQueryParams = {}): Promise<CurrentStock[]> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<CurrentStock>>('/current-stock/', { params });
    return response.data.items || [];
  } catch (error) {
    console.error('Failed to get current stock:', error);
    throw error;
  }
};

export const getItemStock = async (itemId: number): Promise<CurrentStock[]> => {
  return getCurrentStock({ item_id: itemId });
};

// --- Advanced Inventory Features ---

export const getItemStockSummary = async (itemId: number): Promise<ItemStockSummaryResponse> => {
  try {
    const response = await axiosInstance.get<ItemStockSummaryResponse>(`/items/${itemId}/stock-summary`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get stock summary for item ${itemId}:`, error);
    throw error;
  }
};

export const transferStock = async (
  itemId: number, 
  transferData: StockTransferRequest
): Promise<StockTransferResponse> => {
  try {
    // Ensure quantity is sent as string for backend Decimal handling
    const payload = {
      ...transferData,
      quantity: String(transferData.quantity)
    };
    const response = await axiosInstance.post<StockTransferResponse>(
      `/items/${itemId}/transfer-stock`, 
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to transfer stock for item ${itemId}:`, error);
    throw error;
  }
};

export const reconcileStock = async (
  itemId: number,
  reconciliationData: StockReconciliationRequest
): Promise<StockLedgerEntry | null> => {
  try {
    // Ensure physical_quantity is sent as string for backend Decimal handling
    const payload = {
      ...reconciliationData,
      physical_quantity: String(reconciliationData.physical_quantity)
    };
    const response = await axiosInstance.post<StockLedgerEntry | null>(
      `/items/${itemId}/reconcile-stock`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to reconcile stock for item ${itemId}:`, error);
    throw error;
  }
};

// --- Bulk Stock Reconciliation ---

/**
 * Fetches stock sheet for all items in a specific godown
 */
export const getStockSheet = async (godownId: number): Promise<StockSheetItem[]> => {
  try {
    const response = await axiosInstance.get<StockSheetItem[]>('/items/stock-sheet', {
      params: { godown_id: godownId }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch stock sheet:', error);
    throw error;
  }
};

/**
 * Bulk reconciles stock for multiple items in a godown
 */
export const bulkReconcileStock = async (data: BulkReconcileRequest): Promise<BulkReconcileResponse> => {
  try {
    // Convert quantities to strings for backend Decimal handling
    const payload = {
      ...data,
      items: data.items.map(item => ({
        ...item,
        physical_quantity: String(item.physical_quantity)
      }))
    };
    const response = await axiosInstance.post<BulkReconcileResponse>('/items/bulk-reconcile', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to bulk reconcile stock:', error);
    throw error;
  }
};

// --- Stock Ledger Statement ---

/**
 * Fetches stock ledger statement for an item with running balance
 */
export const getItemStockLedgerStatement = async (
  params: StockLedgerStatementParams
): Promise<StockLedgerStatementResponse> => {
  try {
    const { item_id, ...queryParams } = params;
    const response = await axiosInstance.get<StockLedgerStatementResponse>(
      `/stock-ledger/item/${item_id}/statement`,
      { params: queryParams }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to get stock ledger statement for item ${params.item_id}:`, error);
    throw error;
  }
};